/**
 * Calculs financiers CRA : un seul mode de calcul partout.
 * Règle : montant exact par ligne (sans arrondi), somme, puis arrondi du TOTAL à 2 décimales.
 * Utilisé dans : step timesheet, step extra, step facture (= timesheet + extra), step récap.
 * Source unique : le SPA réexporte via l’alias Vite `@activity-report-financial-calculations`.
 */

const normalizeString = (value) => {
  if (value === undefined || value === null) return '';
  return String(value).trim();
};

export const detectTimesheetType = (rawType) => {
  if (!rawType) return null;
  const normalized = normalizeString(rawType)
    .toLowerCase()
    .replace(/[^a-z]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');

  const map = {
    hourly: 'hourly', hour: 'hourly', hours: 'hourly', hour_rate: 'hourly', per_hour: 'hourly',
    hourly_rate: 'hourly', missions_hourly: 'hourly',
    daily: 'daily', day: 'daily', days: 'daily', day_rate: 'daily', per_day: 'daily', daily_rate: 'daily',
    monthly: 'monthly', month: 'monthly', per_month: 'monthly', monthly_rate: 'monthly',
    deliverable: 'deliverable', deliverables: 'deliverable', fixed: 'deliverable', fixed_fee: 'deliverable',
    milestone: 'deliverable', output: 'deliverable',
  };
  if (map[normalized]) return map[normalized];
  if (normalized.includes('deliver')) return 'deliverable';
  if (normalized.includes('month')) return 'monthly';
  if (normalized.includes('day')) return 'daily';
  if (normalized.includes('hour')) return 'hourly';
  return null;
};

export const resolveEntryType = (record) => {
  if (!record) return null;
  if (record.entryType) return record.entryType;
  if (
    record.deliverable ||
    record.deliveredOn ||
    record.delivered_on ||
    record.deliverable_id ||
    record.deliverableId
  ) {
    return 'deliverable';
  }
  if (record.month) return 'monthly';
  if (record.days !== undefined && record.days !== null && record.days !== '') return 'daily';
  if (record.hours !== undefined && record.hours !== null && record.hours !== '') return 'hourly';
  return null;
};

export const inferTimesheetType = (missionType, records = []) => {
  const normalized = detectTimesheetType(missionType);
  if (normalized) return normalized;
  if (records.length > 0) {
    const derived = resolveEntryType(records[0]);
    const normalizedDerived = detectTimesheetType(derived);
    if (normalizedDerived) return normalizedDerived;
    if (derived) return derived;
  }
  return 'hourly';
};

/**
 * Ne conserve que les lignes compatibles avec le modèle de facturation de la mission.
 * Évite qu'une ligne monthly fantôme (ex. import / ancien CRA) s'additionne aux jours daily.
 */
export const filterTimesheetsForBilling = (records = [], missionType) => {
  if (!Array.isArray(records) || records.length === 0) return [];
  const targetType =
    detectTimesheetType(missionType) || inferTimesheetType(missionType, records);
  if (!targetType) return records;
  return records.filter((record) => {
    const rawType = resolveEntryType(record) || record?.type || record?.entryType;
    const entryType = detectTimesheetType(rawType) || (rawType ? String(rawType).toLowerCase() : null);
    // Lignes sans type explicite (legacy) : conservées — seules les lignes typées
    // incompatiblement (ex. monthly fantôme sur mission daily) sont exclues.
    if (!entryType) return true;
    return entryType === targetType;
  });
};

const safeNumber = (value) => {
  if (value === undefined || value === null || value === '') return 0;
  const numeric = Number(value);
  return Number.isNaN(numeric) ? 0 : numeric;
};

/** Arrondi à 2 décimales (un seul point d'arrondi pour tout le flow CRA). */
export const roundTo2 = (value) => {
  if (value === undefined || value === null) return 0;
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
};

const determineEntryType = (record, fallbackType) =>
  detectTimesheetType(record?.entryType) ||
  detectTimesheetType(resolveEntryType(record)) ||
  detectTimesheetType(fallbackType) ||
  fallbackType ||
  'hourly';

const pickRate = (record, defaultRate) => {
  const r = safeNumber(record?.rate);
  if (r > 0) return r;
  const d = safeNumber(defaultRate);
  return d > 0 ? d : 0;
};

/** Palier TJM/THM (effective_date, rate_amount) — pré-calculé côté API CRA. */
export const resolveFreelancerRateFromBreakpoints = (
  breakpoints,
  dateYmd,
  fallbackRate
) => {
  const target =
    dateYmd && typeof dateYmd === 'string' && dateYmd.length >= 10
      ? dateYmd.slice(0, 10)
      : dateYmd;
  if (!target) {
    const fb = safeNumber(fallbackRate);
    return fb > 0 ? fb : undefined;
  }
  if (!Array.isArray(breakpoints) || breakpoints.length === 0) {
    const fb = safeNumber(fallbackRate);
    return fb > 0 ? fb : undefined;
  }
  let resolved;
  const fb = safeNumber(fallbackRate);
  if (fb > 0) resolved = fb;
  for (let i = 0; i < breakpoints.length; i += 1) {
    const bp = breakpoints[i];
    if (!bp?.effective_date) continue;
    if (String(bp.effective_date) <= target) {
      const rate = safeNumber(bp.rate_amount);
      if (rate > 0) resolved = rate;
    } else {
      break;
    }
  }
  return resolved != null && resolved > 0 ? resolved : undefined;
};

const normalizeLineDateYmd = (record, reportMonth) => {
  const raw =
    record?.date ??
    record?.day ??
    record?.timesheet_date ??
    record?.timesheetDate ??
    record?.delivered_on ??
    record?.deliveredOn;
  if (raw) {
    const s = String(raw);
    if (s.includes('T')) return s.split('T')[0].slice(0, 10);
    return s.slice(0, 10);
  }
  if (record?.month) {
    const m = String(record.month).trim();
    if (/^\d{4}-\d{2}$/.test(m)) return `${m}-01`;
    return m.slice(0, 10);
  }
  if (reportMonth && /^\d{4}-\d{2}$/.test(String(reportMonth))) {
    return `${reportMonth}-01`;
  }
  return null;
};

const resolveDefaultRateForLine = (record, options) => {
  let missionId = normalizeString(record?.mission_id ?? record?.missionId);
  if (!missionId && Array.isArray(options.missions) && options.missions.length === 1) {
    const only = options.missions[0];
    missionId = normalizeString(only?.id ?? only?.mission_id ?? only?.missionId);
  }
  const lineDate = normalizeLineDateYmd(record, options.reportMonth);

  if (options.resolveLineDefaultRate && missionId) {
    const fromResolver = options.resolveLineDefaultRate(missionId, record);
    if (fromResolver != null && fromResolver > 0) return fromResolver;
  }

  if (missionId && lineDate && Array.isArray(options.missions)) {
    const mission = options.missions.find(
      (m) => normalizeString(m.id ?? m.mission_id ?? m.missionId) === missionId
    );
    const breakpoints =
      mission?.freelancer_rate_breakpoints ?? mission?.freelancerRateBreakpoints;
    if (breakpoints?.length) {
      const fallback =
        mission.rate_amount ?? mission.fee ?? options.defaultRate;
      const fromBreakpoints = resolveFreelancerRateFromBreakpoints(
        breakpoints,
        lineDate,
        fallback
      );
      if (fromBreakpoints != null && fromBreakpoints > 0) return fromBreakpoints;
    }
  }

  return options.defaultRate;
};

/**
 * Montant exact d'une ligne timesheet (en euros, précision complète).
 * Utilisé uniquement pour sommer puis arrondir dans calculateSystemInvoiceAmount.
 */
export const calculateRecordAmountExact = (record, options = {}) => {
  if (!record) return 0;
  const { fallbackType } = options;
  const defaultRate = resolveDefaultRateForLine(record, options);
  const entryType = determineEntryType(record, fallbackType);

  if (entryType === 'deliverable') {
    if (record.amount !== undefined && record.amount !== null) {
      const cents = safeNumber(record.amount);
      return cents / 100;
    }
    if (record.amountExclVAT !== undefined && record.amountExclVAT !== null) {
      return safeNumber(record.amountExclVAT);
    }
    return pickRate(record, defaultRate);
  }

  if (entryType === 'monthly') {
    if (record.customAmount !== undefined && record.customAmount !== null) {
      return safeNumber(record.customAmount);
    }
    if (record.amount !== undefined && record.amount !== null) {
      return safeNumber(record.amount);
    }
    return pickRate(record, defaultRate);
  }

  const rate = pickRate(record, defaultRate);
  if (!rate) return 0;

  if (entryType === 'daily') return safeNumber(record.days) * rate;
  if (entryType === 'hourly') return safeNumber(record.hours) * rate;

  return rate;
};

export const normalizeMissionBillingContext = (mission) => {
  if (!mission || typeof mission !== 'object') return null;
  const id = normalizeString(mission.id ?? mission.mission_id ?? mission.missionId);
  if (!id) return null;
  const mission_type =
    mission.mission_type ?? mission.missionType ?? mission.pricingType ?? null;
  const rate_amount = mission.rate_amount ?? mission.fee ?? null;
  return { id, mission_type, rate_amount };
};

export const normalizeMissionBillingContexts = (missions) => {
  if (!Array.isArray(missions)) return [];
  return missions.map(normalizeMissionBillingContext).filter(Boolean);
};

const readFrozenBillingAmountExclVat = (record) => {
  const raw = record?.billing_amount_excl_vat ?? record?.billingAmountExclVat;
  if (raw === undefined || raw === null || raw === '') return null;
  const numeric = Number(raw);
  return Number.isFinite(numeric) ? numeric : null;
};

/**
 * CRA multi-missions : chaque ligne timesheet utilise le type et le tarif de sa mission.
 * Évite de filtrer tout le timesheet sur le modèle de la mission principale uniquement.
 */
export const calculateTimesheetAmountForMissions = (records = [], missions = [], legacyOptions = {}) => {
  if (!records.length) return 0;
  const contexts = normalizeMissionBillingContexts(missions);
  if (contexts.length <= 1) {
    const ctx = contexts[0];
    const options =
      contexts.length === 1
        ? {
            ...legacyOptions,
            fallbackType: ctx.mission_type ?? legacyOptions.fallbackType,
            defaultRate: ctx.rate_amount ?? legacyOptions.defaultRate,
          }
        : legacyOptions;
    return calculateSystemInvoiceAmountForSingleMission(records, options);
  }

  const missionById = new Map(contexts.map((context) => [context.id, context]));
  const totalExact = records.reduce((sum, record) => {
    const frozen = readFrozenBillingAmountExclVat(record);
    if (frozen !== null) return sum + frozen;

    let missionId = normalizeString(record?.mission_id ?? record?.missionId);
    if (!missionId && contexts.length === 1) missionId = contexts[0].id;
    const mission = missionById.get(missionId);
    if (!mission) {
      const billingRecords = legacyOptions.fallbackType
        ? filterTimesheetsForBilling([record], legacyOptions.fallbackType)
        : [record];
      if (billingRecords.length === 0) return sum;
      return sum + calculateRecordAmountExact(record, legacyOptions);
    }

    const billingRecords = mission.mission_type
      ? filterTimesheetsForBilling([record], mission.mission_type)
      : [record];
    if (billingRecords.length === 0) return sum;

    const lineOptions = {
      ...legacyOptions,
      fallbackType: mission.mission_type ?? legacyOptions.fallbackType,
      defaultRate: mission.rate_amount ?? legacyOptions.defaultRate,
    };
    lineOptions.defaultRate = resolveDefaultRateForLine(record, lineOptions);
    return sum + calculateRecordAmountExact(record, lineOptions);
  }, 0);
  return roundTo2(totalExact);
};

/** Montant timesheet HT pour une mission (ou legacy sans liste missions). */
export const calculateSystemInvoiceAmountForSingleMission = (records = [], options = {}) => {
  if (!records || records.length === 0) return 0;
  const billingRecords = options.fallbackType
    ? filterTimesheetsForBilling(records, options.fallbackType)
    : records;
  const totalExact = billingRecords.reduce((sum, r) => {
    const lineOptions = {
      ...options,
      defaultRate: resolveDefaultRateForLine(r, options),
    };
    return sum + calculateRecordAmountExact(r, lineOptions);
  }, 0);
  return roundTo2(totalExact);
};

/**
 * Montant timesheet HT (une seule règle : somme exacte puis arrondi à 2 décimales).
 * À utiliser partout : step timesheet, step facture, step récap, view.
 * Passer `options.missions` (≥ 2) pour un CRA multi-missions.
 */
export const calculateSystemInvoiceAmount = (records = [], options = {}) => {
  if (!records || records.length === 0) return 0;
  const missionContexts = normalizeMissionBillingContexts(options.missions);
  if (missionContexts.length > 1) {
    return calculateTimesheetAmountForMissions(records, options.missions, options);
  }
  return calculateSystemInvoiceAmountForSingleMission(records, options);
};

/** Montant total des éléments supplémentaires (arrondi à 2 décimales). */
export const calculateSupplementaryTotal = (supplementaryItems = []) => {
  if (!Array.isArray(supplementaryItems) || supplementaryItems.length === 0) return 0;
  const total = supplementaryItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
  return roundTo2(total);
};

/**
 * Montant facture HT = timesheet + extra (même règle d'arrondi).
 * À utiliser dans step facture et step récap pour afficher le total à facturer.
 */
export const getBillingAmountFromTimesheetAndExtra = (
  timesheetRecords = [],
  supplementaryItems = [],
  options = {}
) => {
  const timesheetTotal = calculateSystemInvoiceAmount(timesheetRecords, options);
  const extraTotal = calculateSupplementaryTotal(supplementaryItems);
  return roundTo2(timesheetTotal + extraTotal);
};

/**
 * Montant facturé HT d'un CRA = timesheet + éléments supplémentaires (+ frais refacturés).
 *
 * ⚠️ POINT CRITIQUE (comptabilité) : ce montant alimente `invoice_amount_excl_vat`,
 * source de vérité des écritures comptables (accounting_entries), de la vérification
 * de facture et du reporting. Les éléments supplémentaires (lignes refacturées)
 * DOIVENT être inclus, sinon l'accounting est sous-évalué.
 *
 * `refacturedExpensesBase` (optionnel) = base HT des dépenses refacturées (mode
 * 'refacturation'), calculée via calculateRefacturedExpensesBase. Cette base subit
 * ensuite le taux de TVA de la prestation (accessoire suit le principal, CGI art. 267).
 *
 * Renvoie 0 quand il n'y a ni timesheet, ni élément supplémentaire, ni frais refacturé
 * (CRA néant). Verrouillé par activityReportFinancialCalculations.test.ts (anti-régression).
 */
export const resolveInvoiceAmountExclVat = (
  timesheetRecords = [],
  supplementaryItems = [],
  options = {},
  refacturedExpensesBase = 0
) => {
  const hasTimesheet = Array.isArray(timesheetRecords) && timesheetRecords.length > 0;
  const hasSupplementary = Array.isArray(supplementaryItems) && supplementaryItems.length > 0;
  const refacturedBase = roundTo2(safeNumber(refacturedExpensesBase));
  if (!hasTimesheet && !hasSupplementary && refacturedBase <= 0) return 0;
  const prestation = getBillingAmountFromTimesheetAndExtra(
    timesheetRecords || [],
    supplementaryItems || [],
    options
  );
  return roundTo2(prestation + refacturedBase);
};

// --- Rétrocompatibilité (utilisés par calculateExpenseTotals et calculateTimesheetRecordAmount) ---

const floorToCents = (value) => {
  if (value === undefined || value === null) return 0;
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.floor(n * 100 + 1e-6);
};

const calculateRecordAmountCents = (record, options = {}) => {
  const exact = calculateRecordAmountExact(record, options);
  return floorToCents(exact);
};

/** Montant d'une ligne pour affichage / compat (en euros). */
export const calculateTimesheetRecordAmount = (record, options = {}) =>
  calculateRecordAmountCents(record, options) / 100;

// --- Dépenses (inchangé : floor par ligne puis somme) ---

const resolveExpenseAmountExcl = (record) => {
  const direct = safeNumber(record?.amountExclVAT ?? record?.amount_excl_vat);
  if (direct) return direct;
  const incl = safeNumber(record?.amountInclVAT ?? record?.amount_incl_vat);
  const vat = safeNumber(record?.vatAmount ?? record?.vat_amount);
  if (incl && vat) {
    const diff = incl - vat;
    return diff > 0 ? diff : 0;
  }
  return incl;
};

const resolveExpenseVat = (record) => {
  const direct = safeNumber(record?.vatAmount ?? record?.vat_amount);
  if (direct) return direct;
  const incl = safeNumber(record?.amountInclVAT ?? record?.amount_incl_vat);
  const excl = safeNumber(record?.amountExclVAT ?? record?.amount_excl_vat);
  if (incl && excl) {
    const diff = incl - excl;
    return diff > 0 ? diff : 0;
  }
  return 0;
};

const resolveExpenseAmountIncl = (record) => {
  const direct = safeNumber(record?.amountInclVAT ?? record?.amount_incl_vat);
  if (direct) return direct;
  const excl = safeNumber(record?.amountExclVAT ?? record?.amount_excl_vat);
  const vat = safeNumber(record?.vatAmount ?? record?.vat_amount);
  return excl + vat;
};

export const calculateExpenseTotals = (records = []) => {
  const totals = records.reduce(
    (acc, record) => {
      acc.amountExclCents += floorToCents(resolveExpenseAmountExcl(record));
      acc.vatCents += floorToCents(resolveExpenseVat(record));
      return acc;
    },
    { amountExclCents: 0, vatCents: 0 }
  );
  return {
    amountExclVAT: totals.amountExclCents / 100,
    vatTotal: totals.vatCents / 100,
  };
};

// --- Refacturation de frais (CGI art. 267 : l'accessoire suit le principal) ---

export const EXPENSE_BILLING_MODE = {
  DEBOURS: 'debours',
  REFACTURATION: 'refacturation',
};

/** Mode de facturation d'une dépense (défaut : refacturation pour les nouvelles dépenses). */
export const getExpenseBillingMode = (record) => {
  const raw = record?.billingMode ?? record?.billing_mode;
  return raw === EXPENSE_BILLING_MODE.DEBOURS
    ? EXPENSE_BILLING_MODE.DEBOURS
    : EXPENSE_BILLING_MODE.REFACTURATION;
};

export const isRefacturationExpense = (record) =>
  getExpenseBillingMode(record) === EXPENSE_BILLING_MODE.REFACTURATION;

export const isDeboursExpense = (record) =>
  getExpenseBillingMode(record) === EXPENSE_BILLING_MODE.DEBOURS;

/** TVA de la dépense récupérable pour le freelance (défaut : true). */
export const isExpenseVatRecoverable = (record) => {
  const raw = record?.vatRecoverable ?? record?.vat_recoverable;
  return raw === false ? false : true;
};

export const filterExpensesByBillingMode = (records = [], mode) =>
  (Array.isArray(records) ? records : []).filter((r) => getExpenseBillingMode(r) === mode);

/**
 * Base HT à refacturer pour UNE dépense (mode refacturation).
 * Règle (CGI art. 267) :
 *  - freelance non assujetti (ou 0 %)      -> base = TTC (pas de TVA à récupérer).
 *  - assujetti ET TVA récupérable          -> base = HT (il récupère la TVA en amont).
 *  - assujetti ET TVA NON récupérable      -> base = TTC (transport de personnes, hôtel…).
 * Le taux de TVA appliqué ensuite est celui de la prestation, jamais celui de l'achat.
 */
export const resolveRefacturedExpenseBase = (
  record,
  { freelancerAssujetti = false, previewBeforeFreelancerVatSelection = false } = {},
) => {
  const ht = resolveExpenseAmountExcl(record);
  const ttc = resolveExpenseAmountIncl(record);
  // Étape frais avant le choix TVA CRA : aperçu piloté par « TVA récupérable » uniquement.
  if (previewBeforeFreelancerVatSelection) {
    return isExpenseVatRecoverable(record) ? ht : ttc;
  }
  if (!freelancerAssujetti) return ttc;
  if (isExpenseVatRecoverable(record)) return ht;
  return ttc;
};

/**
 * Somme des bases HT des dépenses refacturées (mode refacturation uniquement).
 * Cette base est ajoutée au montant facturé HT (resolveInvoiceAmountExclVat) et
 * subit le taux de TVA de la prestation.
 */
export const calculateRefacturedExpensesBase = (
  records = [],
  { freelancerAssujetti = false, previewBeforeFreelancerVatSelection = false } = {},
) => {
  const refac = filterExpensesByBillingMode(records, EXPENSE_BILLING_MODE.REFACTURATION);
  const options = { freelancerAssujetti, previewBeforeFreelancerVatSelection };
  const cents = refac.reduce(
    (sum, record) => sum + floorToCents(resolveRefacturedExpenseBase(record, options)),
    0,
  );
  return cents / 100;
};

// --- Débours (note de frais séparée, % remboursement sur TTC) ---

/** @returns {number} Percent in (0, 100], default 100. */
export const resolveExpenseReimbursementPercent = (record) => {
  const raw = record?.reimbursementPercent ?? record?.reimbursement_percent;
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return 100;
  return Math.min(100, Math.round(n * 100) / 100);
};

const scaleCentsByPercent = (cents, percent) =>
  Math.floor((cents * percent) / 100);

/**
 * Dérive le % à persister à partir du TTC ligne et du TTC remboursable saisi,
 * pour que {@link resolveDeboursReimbursedLineCents} retrouve le même TTC remboursé (centimes).
 *
 * @param {number} fullTtc
 * @param {number} reimbursableTtc
 * @returns {number}
 */
export const deriveReimbursementPercentFromTtcAmounts = (fullTtc, reimbursableTtc) => {
  const ttcCents = floorToCents(fullTtc);
  const targetCents = floorToCents(reimbursableTtc);
  if (ttcCents <= 0) return 100;
  if (targetCents <= 0) return 0.01;
  if (targetCents >= ttcCents) return 100;

  const pLow = (targetCents * 100) / ttcCents;
  const pHigh = ((targetCents + 1) * 100) / ttcCents;

  let p = Math.ceil(pLow * 100 - 1e-9) / 100;
  const maxP = Math.min(100, Math.floor((pHigh - 1e-9) * 100) / 100);

  while (p <= maxP + 1e-9 && p <= 100) {
    if (scaleCentsByPercent(ttcCents, p) === targetCents) {
      return Math.round(p * 100) / 100;
    }
    p = Math.round((p + 0.01) * 100) / 100;
  }

  p = Math.ceil(pLow * 100 - 1e-9) / 100;
  while (p <= 100) {
    if (scaleCentsByPercent(ttcCents, p) >= targetCents) {
      return Math.min(100, Math.round(p * 100) / 100);
    }
    p = Math.round((p + 0.01) * 100) / 100;
  }

  return 100;
};

/**
 * % de remboursement appliqué sur le **TTC** de la ligne ; HT/TVA remboursés au prorata.
 * @returns {{ amountExclCents: number, vatCents: number, amountInclCents: number }}
 */
export const resolveDeboursReimbursedLineCents = (record, percentOverride) => {
  const percent =
    percentOverride != null
      ? resolveExpenseReimbursementPercent({ reimbursement_percent: percentOverride })
      : resolveExpenseReimbursementPercent(record);
  const htCents = floorToCents(resolveExpenseAmountExcl(record));
  const vatCents = floorToCents(resolveExpenseVat(record));
  let ttcCents = floorToCents(resolveExpenseAmountIncl(record));
  if (ttcCents <= 0) ttcCents = htCents + vatCents;
  const reimbursedTtcCents = scaleCentsByPercent(ttcCents, percent);
  if (reimbursedTtcCents <= 0 || ttcCents <= 0) {
    return { amountExclCents: 0, vatCents: 0, amountInclCents: 0 };
  }
  const amountExclCents = Math.floor((reimbursedTtcCents * htCents) / ttcCents);
  const vatReimbCents = reimbursedTtcCents - amountExclCents;
  return {
    amountExclCents,
    vatCents: vatReimbCents,
    amountInclCents: reimbursedTtcCents,
  };
};

/**
 * Totaux remboursés pour les débours uniquement (% appliqué sur le TTC de chaque ligne).
 */
export const calculateDeboursReimbursedTotals = (records = []) => {
  const debours = filterExpensesByBillingMode(records, EXPENSE_BILLING_MODE.DEBOURS);
  const totals = debours.reduce(
    (acc, record) => {
      const line = resolveDeboursReimbursedLineCents(record);
      acc.amountExclCents += line.amountExclCents;
      acc.vatCents += line.vatCents;
      return acc;
    },
    { amountExclCents: 0, vatCents: 0 },
  );
  return {
    amountExclVAT: totals.amountExclCents / 100,
    vatTotal: totals.vatCents / 100,
  };
};

export const hasDeboursExpenses = (records = []) =>
  filterExpensesByBillingMode(records, EXPENSE_BILLING_MODE.DEBOURS).length > 0;

/**
 * Mode frais effectif d'un CRA : priorité aux lignes persistées (billing_mode),
 * pas au réglage équipe courant (équipe passée en débours ≠ requalifier les CRA refacturation).
 *
 * @param {unknown[] | null | undefined} expenseData
 * @param {'none' | 'refacturation' | 'debours'} teamExpenseCraMode
 * @returns {'none' | 'refacturation' | 'debours'}
 */
export const resolveExpenseCraModeForReport = (expenseData, teamExpenseCraMode) => {
  const expenses = Array.isArray(expenseData) ? expenseData : [];
  if (expenses.length === 0) {
    return teamExpenseCraMode;
  }

  const deboursCount = filterExpensesByBillingMode(expenses, EXPENSE_BILLING_MODE.DEBOURS).length;
  const refacturationCount = filterExpensesByBillingMode(
    expenses,
    EXPENSE_BILLING_MODE.REFACTURATION,
  ).length;

  if (refacturationCount > 0) {
    return 'refacturation';
  }
  if (deboursCount > 0) {
    return 'debours';
  }

  return teamExpenseCraMode;
};

/** Activité facturable CRA : timesheet, supplémentaires, ou frais refacturés (pas les débours seuls). */
export const hasBillableActivityInReport = (params) => {
  const timesheetData = params?.timesheetData ?? params?.timesheets;
  const supplementaryItems = params?.supplementaryItems;
  const expenseData = params?.expenseData ?? params?.expenses;
  const hasRefacturedExpenses =
    filterExpensesByBillingMode(expenseData || [], EXPENSE_BILLING_MODE.REFACTURATION).length > 0;
  return (
    (timesheetData?.length || 0) > 0 ||
    (supplementaryItems?.length || 0) > 0 ||
    hasRefacturedExpenses
  );
};
